import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PlateMindLogo } from "@/components/PlateMindLogo";
import { 
  Camera, Activity, Brain, ChevronRight, Search, LineChart, MessageCircle,
  Globe, Lightbulb, Target, Apple, BarChart3, Utensils, Smartphone, Share, 
  Plus, MoreVertical, Download, Zap
} from "lucide-react";

// Lana Avatar Image URL
const LANA_AVATAR_URL = "https://customer-assets.emergentagent.com/job_glucose-insights-2/artifacts/y7q9fk4i_ChatGPT%20Image%20Mar%205%2C%202026%2C%2004_39_46%20AM.png";

const Landing = () => {
  const { t, tArray } = useLanguage();

  const problemQuestions = tArray('landing.problem_questions');
  const meetLanaQuestions = tArray('landing.meet_lana_questions');
  const trustFeatures = tArray('landing.trust_features');

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation - Premium Health-Tech Style Header */}
      <nav className="sticky top-0 z-50 w-full bg-background/95 backdrop-blur-lg border-b border-orange-100/50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 md:h-18">
          <div className="h-full flex items-center justify-between">
            {/* Left: Logo + Brand */}
            <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
              <PlateMindLogo size={44} className="drop-shadow-sm" />
              <span className="font-heading font-bold text-xl md:text-[22px] text-secondary tracking-tight">
                platemind
              </span>
            </Link>
            
            {/* Right: Language Selector + Login */}
            <div className="flex items-center gap-2 md:gap-3">
              <LanguageSwitcher variant="pill" />
              <Link to="/login">
                <Button 
                  data-testid="nav-login-btn" 
                  className="rounded-full bg-primary text-white hover:bg-primary/90 px-4 md:px-5 h-[38px] font-semibold text-sm shadow-md hover:shadow-lg transition-all duration-200"
                >
                  {t('nav.login')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-slide-up">
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary tracking-tight leading-tight">
                {t('landing.hero_title')}
                <span className="text-primary"> {t('landing.hero_title_accent')}</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-lg">
                {t('landing.hero_subtitle')}
              </p>
              <div className="pt-4">
                <Link to="/signup">
                  <Button 
                    data-testid="hero-analyze-btn"
                    className="rounded-full bg-primary text-white hover:bg-primary/90 px-8 py-6 font-bold shadow-button-primary hover:shadow-button-primary-hover transition-all transform hover:-translate-y-1"
                  >
                    {t('landing.hero_cta')}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              
              {/* Hero Badge - PWA Install Hint */}
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full">
                  <Smartphone className="w-3.5 h-3.5" />
                  {t('landing.hero_badge_mobile')}
                </span>
                <span className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 text-xs font-medium px-3 py-1.5 rounded-full">
                  <Zap className="w-3.5 h-3.5" />
                  {t('landing.hero_badge_install')}
                </span>
              </div>
            </div>
            
            {/* Hero Visual - Mock UI Card */}
            <div className="relative animate-fade-in">
              <Card className="bg-white rounded-3xl border border-orange-100/50 shadow-soft-lift p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
                    <Utensils className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-secondary">Jollof Rice + Chicken</p>
                    <p className="text-sm text-muted-foreground">{t('landing.hero_meal_detected')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-orange-50 rounded-2xl p-3 text-center">
                    <p className="text-2xl font-bold text-primary">65g</p>
                    <p className="text-xs text-muted-foreground">{t('meal_analysis.carbs')}</p>
                  </div>
                  <div className="bg-orange-50 rounded-2xl p-3 text-center">
                    <p className="text-2xl font-bold text-warning">{t('glucose.high')}</p>
                    <p className="text-xs text-muted-foreground">{t('meal_analysis.gi_impact')}</p>
                  </div>
                  <div className="bg-orange-50 rounded-2xl p-3 text-center">
                    <p className="text-2xl font-bold text-secondary">520</p>
                    <p className="text-xs text-muted-foreground">{t('meal_analysis.calories')}</p>
                  </div>
                </div>
                <div className="bg-primary/5 rounded-2xl p-4">
                  <p className="text-sm text-secondary leading-relaxed">
                    <span className="font-semibold">{t('landing.ai_tip_label')}:</span> {t('landing.ai_tip_text')}
                  </p>
                </div>
              </Card>
              <div className="absolute -z-10 -bottom-4 -right-4 w-full h-full bg-primary/10 rounded-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-secondary tracking-tight">
            {t('landing.problem_title')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t('landing.problem_subtitle')}
          </p>
          <div className="grid md:grid-cols-3 gap-6 pt-4">
            {problemQuestions.map((question, index) => (
              <Card key={index} className="bg-orange-50 rounded-3xl p-6 border-none">
                <p className="text-lg font-medium text-secondary">{question}</p>
              </Card>
            ))}
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto pt-4">
            {t('landing.problem_solution')}
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-secondary tracking-tight">
              {t('landing.how_it_works')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.how_it_works_subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Search,
                step: `${t('landing.step_label')} 1`,
                title: t('landing.step1_title'),
                description: t('landing.step1_desc')
              },
              {
                icon: Lightbulb,
                step: `${t('landing.step_label')} 2`,
                title: t('landing.step2_title'),
                description: t('landing.step2_desc')
              },
              {
                icon: Activity,
                step: `${t('landing.step_label')} 3`,
                title: t('landing.step3_title'),
                description: t('landing.step3_desc')
              }
            ].map((item, index) => (
              <Card key={index} className="bg-white rounded-3xl border border-orange-100/50 p-8 hover:shadow-soft-lift transition-all duration-300 hover:-translate-y-1">
                <div className="space-y-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-7 h-7 text-primary" />
                  </div>
                  <p className="text-sm font-bold text-primary uppercase tracking-wider">{item.step}</p>
                  <h3 className="font-heading text-xl font-semibold text-secondary">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-6xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-secondary tracking-tight">
              {t('landing.features')}
            </h2>
            <p className="text-lg text-muted-foreground">{t('landing.features_subtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Search,
                title: t('landing.feature_meal_title'),
                description: t('landing.feature_meal_desc')
              },
              {
                icon: LineChart,
                title: t('landing.feature_glucose_title'),
                description: t('landing.feature_glucose_desc')
              },
              {
                icon: MessageCircle,
                title: t('landing.feature_ai_title'),
                description: t('landing.feature_ai_desc')
              },
              {
                icon: Globe,
                title: t('landing.feature_global_title'),
                description: t('landing.feature_global_desc')
              }
            ].map((feature, index) => (
              <Card key={index} className="bg-orange-50 rounded-3xl p-8 border-none hover:bg-orange-100/50 transition-colors duration-300">
                <div className="space-y-4">
                  <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-secondary">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Lana Section */}
      <section className="py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-secondary tracking-tight">
                {t('landing.meet_lana_title')}
              </h2>
              <p className="text-xl text-primary font-medium">
                {t('landing.meet_lana_subtitle')}
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('landing.meet_lana_desc')}
              </p>
              <div className="space-y-3 pt-4">
                <p className="text-sm font-semibold text-secondary uppercase tracking-wider">Example questions:</p>
                {meetLanaQuestions.map((question, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <span className="text-primary mt-1">•</span>
                    <p className="text-muted-foreground">{question}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-primary/20 shadow-2xl">
                  <img 
                    src={LANA_AVATAR_URL} 
                    alt="Lana - Virtual Dietitian" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <Card className="absolute -bottom-4 -right-4 bg-white rounded-2xl p-4 shadow-soft-lift border border-orange-100/50">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-primary" />
                    <span className="text-sm font-medium text-secondary">Lana</span>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto flex items-center justify-center">
            <Brain className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-secondary tracking-tight">
            {t('landing.trust_title')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            {t('landing.trust_desc')}
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            {trustFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 bg-orange-50 rounded-full px-4 py-2">
                <Target className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-secondary">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-gradient-to-br from-primary/5 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-secondary tracking-tight mb-12">
            {t('landing.final_cta_title')}
          </h2>
          <Link to="/signup">
            <Button 
              data-testid="final-cta-btn"
              className="rounded-full bg-primary text-white hover:bg-primary/90 px-10 py-6 font-bold text-lg shadow-button-primary hover:shadow-button-primary-hover transition-all transform hover:-translate-y-1"
            >
              {t('landing.final_cta_button')}
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-orange-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center text-center space-y-6">
            <div className="flex items-center gap-2">
              <PlateMindLogo size={40} />
              <span className="font-heading font-bold text-xl text-secondary">platemind</span>
            </div>
            <p className="text-muted-foreground">
              {t('landing.footer_tagline')}
            </p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-secondary transition-colors">
                {t('landing.privacy')}
              </Link>
              <Link to="/contact" className="text-muted-foreground hover:text-secondary transition-colors">
                {t('landing.contact')}
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-secondary transition-colors">
                {t('landing.terms')}
              </Link>
            </div>
            
            {/* Data Source & Designed By */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500 pt-4">
              <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                {t('landing.footer_designed_by')}
              </span>
              <span>
                {t('landing.footer_data_source')}
              </span>
            </div>
            
            {/* Medical Disclaimer */}
            <div className="max-w-2xl pt-4 border-t border-orange-100">
              <p className="text-xs text-gray-400 leading-relaxed">
                {t('landing.footer_disclaimer')}
              </p>
            </div>
            
            {/* Copyright */}
            <p className="text-xs text-gray-400">
              © {new Date().getFullYear()} PlateMind. {t('landing.footer_copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
